import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import Axios from 'axios';
import {
  Box,
  Grid,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronLeftOutlined,
  ViewModule,
  CalendarMonth,
  FilterList,
  Clear,
  Storefront,
  Event as EventIcon,
  Check,
  AutoAwesome,
  LocationOn,
  CalendarToday,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Payment,
  FilterAlt,
} from '@mui/icons-material';
import fayvenDashImage from '../../../../assets/img/fayvenDashImage.png';
import { CustomButton, Layout, Loading, UserNavbar, LinkButton } from '../../../../components';
import { APP_ROUTES } from '../../../../constants/app-routes';
import DashboardSearchBar from './components/DashboardSearchBar';
import EventList from './components/EventList';
import CalendarView from './components/CalendarView';
import RetailSpacesList from './components/RetailSpacesList';
import PastEvents from '../components/PastEvents';
import { getHeaders } from '../../../../commons/api/headers';
import * as RetailProfileActions from '../../../../core/retail-profiles/action';
import { getM2MAccessToken, getApiUrl } from '../../../../service/ExternalApiTokenService';

function VendorDashboard() {
  const dispatch = useDispatch();
  const { filteredEvents, loading: eventsLoading } = useSelector((store) => store.events);
  const { user } = useSelector((store) => store.users);
  const [isPastEventsVisible, setIsPastEventsVisible] = useState(false);

  // Load view mode from localStorage, default to 'grid' if not set
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('vendorDashboardViewMode');
    return savedViewMode || 'grid';
  });

  // Get retail profiles from Redux
  const { profiles: retailProfiles, loading: retailProfilesLoading } = useSelector(
    (store) => store.retailProfiles,
  );

  // Combined loading state - wait for both events and retail profiles to finish loading
  const isLoading = eventsLoading || retailProfilesLoading;

  // Filter input states (what user is typing/selecting)
  const [venueFilter, setVenueFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('both'); // 'both', 'event', 'retail'
  const [indoorOutdoorFilter, setIndoorOutdoorFilter] = useState('both'); // 'both', 'indoor', 'outdoor' (retail only)
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [minPriceFilter, setMinPriceFilter] = useState('');
  const [maxPriceFilter, setMaxPriceFilter] = useState('');

  // Applied filter states (what is actually being used for filtering)
  const [appliedVenueFilter, setAppliedVenueFilter] = useState('');
  const [appliedCityFilter, setAppliedCityFilter] = useState('');
  const [appliedStateFilter, setAppliedStateFilter] = useState('');
  const [appliedTypeFilter, setAppliedTypeFilter] = useState('both');
  const [appliedIndoorOutdoorFilter, setAppliedIndoorOutdoorFilter] = useState('both');
  const [appliedStartDateFilter, setAppliedStartDateFilter] = useState('');
  const [appliedEndDateFilter, setAppliedEndDateFilter] = useState('');
  const [appliedMinPriceFilter, setAppliedMinPriceFilter] = useState('');
  const [appliedMaxPriceFilter, setAppliedMaxPriceFilter] = useState('');

  // AI recommendations state
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendationsExpanded, setAiRecommendationsExpanded] = useState(true);
  const [aiRecommendationsTab, setAiRecommendationsTab] = useState('events'); // 'events', 'retail'
  const aiRequestIdRef = useRef(0); // Track current AI request to prevent race conditions

  // Simplified color scheme
  const EVENT_COLOR = '#EE7B57'; // Orange for events
  const RETAIL_COLOR = '#3c99ba'; // Turquoise for retail

  // Check if any filters are currently applied (excluding type filter)
  const hasFiltersApplied = useMemo(
    () =>
      appliedVenueFilter ||
      appliedCityFilter ||
      appliedStateFilter ||
      appliedStartDateFilter ||
      appliedEndDateFilter ||
      appliedMinPriceFilter ||
      appliedMaxPriceFilter ||
      appliedIndoorOutdoorFilter !== 'both',
    [
      appliedVenueFilter,
      appliedCityFilter,
      appliedStateFilter,
      appliedStartDateFilter,
      appliedEndDateFilter,
      appliedMinPriceFilter,
      appliedMaxPriceFilter,
      appliedIndoorOutdoorFilter,
    ],
  );

  // Apply filters to events
  const filteredEventsByLocation = useMemo(() => {
    if (!filteredEvents || filteredEvents.length === 0) return [];

    let filtered = filteredEvents;

    // Filter out past events (only show events from today onwards)
    const today = moment().startOf('day');
    filtered = filtered.filter((event) => {
      const eventDate = moment(event.parameters_event?.event_date);
      if (!eventDate.isValid()) return false; // Exclude events with invalid dates
      return eventDate.startOf('day').isSameOrAfter(today);
    });

    // Filter by venue
    if (appliedVenueFilter.trim()) {
      const searchTerm = appliedVenueFilter.toLowerCase();
      filtered = filtered.filter((event) => {
        const venueName = event.venue?.name?.toLowerCase() || '';
        const venueAddress = event.venue?.business_address?.toLowerCase() || '';
        return venueName.includes(searchTerm) || venueAddress.includes(searchTerm);
      });
    }

    // Filter by city
    if (appliedCityFilter.trim()) {
      const searchTerm = appliedCityFilter.toLowerCase();
      filtered = filtered.filter((event) => {
        // Check both direct city and venue.city paths
        const cityName = event.city?.name?.toLowerCase() || '';
        const venueCityName = event.venue?.city?.name?.toLowerCase() || '';
        return cityName.includes(searchTerm) || venueCityName.includes(searchTerm);
      });
    }

    // Filter by state
    if (appliedStateFilter.trim()) {
      const searchTerm = appliedStateFilter.toLowerCase();
      filtered = filtered.filter((event) => {
        // Check both direct state and venue.state paths
        const stateName = event.state?.name?.toLowerCase() || '';
        const stateCode = event.state?.code?.toLowerCase() || '';
        const venueStateName = event.venue?.state?.name?.toLowerCase() || '';
        const venueStateCode = event.venue?.state?.code?.toLowerCase() || '';
        return (
          stateName.includes(searchTerm) ||
          stateCode.includes(searchTerm) ||
          venueStateName.includes(searchTerm) ||
          venueStateCode.includes(searchTerm)
        );
      });
    }

    // Filter by date range
    if (appliedStartDateFilter || appliedEndDateFilter) {
      filtered = filtered.filter((event) => {
        const eventDate = moment(event.parameters_event?.event_date);
        if (!eventDate.isValid()) return false;

        const eventDateOnly = eventDate.startOf('day');

        // If start date is set, event must be on or after it
        if (appliedStartDateFilter) {
          const startDate = moment(appliedStartDateFilter).startOf('day');
          if (eventDateOnly.isBefore(startDate)) return false;
        }

        // If end date is set, event must be on or before it
        if (appliedEndDateFilter) {
          const endDate = moment(appliedEndDateFilter).startOf('day');
          if (eventDateOnly.isAfter(endDate)) return false;
        }

        return true;
      });
    }

    // Filter by indoor/outdoor (space_type)
    if (appliedIndoorOutdoorFilter !== 'both') {
      filtered = filtered.filter(
        (event) => event.space_type?.toLowerCase() === appliedIndoorOutdoorFilter.toLowerCase(),
      );
    }

    // Filter by price range (calculate price per hour for events based on duration)
    if (appliedMinPriceFilter || appliedMaxPriceFilter) {
      const minPrice = appliedMinPriceFilter ? parseFloat(appliedMinPriceFilter) : 0;
      const maxPrice = appliedMaxPriceFilter ? parseFloat(appliedMaxPriceFilter) : Infinity;

      filtered = filtered.filter((event) => {
        // Check if event has space_dimensions
        if (!event.space_dimensions || event.space_dimensions.length === 0) {
          // If no space dimensions, exclude from results when price filter is applied
          return false;
        }

        // Calculate event duration in hours
        const startTime = event.parameters_event?.hour_start_event;
        const endTime = event.parameters_event?.hour_end_event;
        let durationHours = 1; // Default to 1 hour if times are not available

        if (startTime && endTime) {
          const eventDate = event.parameters_event?.event_date || new Date();
          const start = moment(`${moment(eventDate).format('YYYY-MM-DD')} ${startTime}`);
          const end = moment(`${moment(eventDate).format('YYYY-MM-DD')} ${endTime}`);

          if (start.isValid() && end.isValid()) {
            durationHours = end.diff(start, 'hours', true); // Get decimal hours
            if (durationHours <= 0) durationHours = 1; // Ensure positive duration
          }
        }

        // Check if any space_dimension price per hour falls within the price range
        return event.space_dimensions.some((dimension) => {
          const totalCost = parseFloat(dimension.cost) || 0;
          const pricePerHour = totalCost / durationHours;
          return pricePerHour >= minPrice && pricePerHour <= maxPrice;
        });
      });
    }

    // Sort by event date (soonest first)
    filtered.sort((a, b) => {
      const dateA = moment(a.parameters_event?.event_date);
      const dateB = moment(b.parameters_event?.event_date);

      if (!dateA.isValid()) return 1; // Push invalid dates to end
      if (!dateB.isValid()) return -1;

      return dateA.diff(dateB); // Ascending order (soonest first)
    });

    return filtered;
  }, [
    filteredEvents,
    appliedVenueFilter,
    appliedCityFilter,
    appliedStateFilter,
    appliedIndoorOutdoorFilter,
    appliedStartDateFilter,
    appliedEndDateFilter,
    appliedMinPriceFilter,
    appliedMaxPriceFilter,
  ]);

  // Apply filters to retail profiles
  const filteredRetailProfilesByLocation = useMemo(() => {
    if (!retailProfiles || retailProfiles.length === 0) return [];

    let filtered = retailProfiles;

    // Filter out past retail opportunities (only show ones that are still active or ongoing)
    const today = moment().startOf('day');
    filtered = filtered.filter((profile) => {
      // If profile has an end_date and it's in the past, exclude it
      if (profile.end_date) {
        const endDate = moment(profile.end_date).startOf('day');
        if (endDate.isBefore(today)) return false;
      }
      // Profiles without end_date or with future/today end_date are included
      return true;
    });

    // Filter by venue
    if (appliedVenueFilter.trim()) {
      const searchTerm = appliedVenueFilter.toLowerCase();
      filtered = filtered.filter((profile) => {
        const venueName = profile.venue?.name?.toLowerCase() || '';
        const venueAddress = profile.venue?.business_address?.toLowerCase() || '';
        return venueName.includes(searchTerm) || venueAddress.includes(searchTerm);
      });
    }

    // Filter by city
    if (appliedCityFilter.trim()) {
      const searchTerm = appliedCityFilter.toLowerCase();
      filtered = filtered.filter((profile) => {
        const venueCityName = profile.venue?.city?.name?.toLowerCase() || '';
        return venueCityName.includes(searchTerm);
      });
    }

    // Filter by state
    if (appliedStateFilter.trim()) {
      const searchTerm = appliedStateFilter.toLowerCase();
      filtered = filtered.filter((profile) => {
        const venueStateName = profile.venue?.state?.name?.toLowerCase() || '';
        const venueStateCode = profile.venue?.state?.code?.toLowerCase() || '';
        return venueStateName.includes(searchTerm) || venueStateCode.includes(searchTerm);
      });
    }

    // Filter by indoor/outdoor (only if retail is selected or both)
    if (appliedIndoorOutdoorFilter !== 'both') {
      filtered = filtered.filter((profile) => {
        if (!profile.schedules || profile.schedules.length === 0) return false;
        // Check if any schedule matches the space type
        return profile.schedules.some(
          (schedule) =>
            schedule.space_type?.toLowerCase() === appliedIndoorOutdoorFilter.toLowerCase(),
        );
      });
    }

    // Filter by date range
    if (appliedStartDateFilter || appliedEndDateFilter) {
      filtered = filtered.filter((profile) => {
        const profileStart = profile.start_date ? moment(profile.start_date) : null;
        const profileEnd = profile.end_date ? moment(profile.end_date) : null;

        // If profile has no dates, it's considered ongoing, so it matches any date range
        if (!profileStart && !profileEnd) return true;

        // If start date filter is set, profile end must be on or after it (or no end date)
        if (appliedStartDateFilter) {
          const startDate = moment(appliedStartDateFilter).startOf('day');
          if (profileEnd) {
            if (profileEnd.isBefore(startDate)) return false;
          }
        }

        // If end date filter is set, profile start must be on or before it (or no start date)
        if (appliedEndDateFilter) {
          const endDate = moment(appliedEndDateFilter).startOf('day');
          if (profileStart) {
            if (profileStart.isAfter(endDate)) return false;
          }
        }

        return true;
      });
    }

    // Filter by price range (for retail profiles, this will be applied per-day in calendar view)
    // Here we just check if profile has any schedules within price range
    if (appliedMinPriceFilter || appliedMaxPriceFilter) {
      const minPrice = appliedMinPriceFilter ? parseFloat(appliedMinPriceFilter) : 0;
      const maxPrice = appliedMaxPriceFilter ? parseFloat(appliedMaxPriceFilter) : Infinity;

      filtered = filtered.filter((profile) => {
        if (!profile.schedules || profile.schedules.length === 0) return false;

        // Check if any schedule has price_per_hour within the range
        return profile.schedules.some((schedule) => {
          const price = parseFloat(schedule.price_per_hour) || 0;
          return price >= minPrice && price <= maxPrice;
        });
      });
    }

    return filtered;
  }, [
    retailProfiles,
    appliedVenueFilter,
    appliedCityFilter,
    appliedStateFilter,
    appliedIndoorOutdoorFilter,
    appliedStartDateFilter,
    appliedEndDateFilter,
    appliedMinPriceFilter,
    appliedMaxPriceFilter,
  ]);

  // Apply filters - copies input values to applied values
  const applyFilters = async () => {
    setAppliedVenueFilter(venueFilter);
    setAppliedCityFilter(cityFilter);
    setAppliedStateFilter(stateFilter);
    setAppliedTypeFilter(typeFilter);
    setAppliedIndoorOutdoorFilter(indoorOutdoorFilter);
    setAppliedStartDateFilter(startDateFilter);
    setAppliedEndDateFilter(endDateFilter);
    setAppliedMinPriceFilter(minPriceFilter);
    setAppliedMaxPriceFilter(maxPriceFilter);

    // Check if only venue name is entered (AI doesn't use venue name for matching)
    const hasOnlyVenueFilter =
      venueFilter &&
      !cityFilter &&
      !stateFilter &&
      !startDateFilter &&
      !endDateFilter &&
      !minPriceFilter &&
      !maxPriceFilter &&
      indoorOutdoorFilter === 'both';

    // Call external API with filter data (skip if only venue name is entered)
    if (hasOnlyVenueFilter) {
      setAiRecommendations(null);
      setAiRecommendationsExpanded(false);
      return;
    }

    setAiLoading(true);
    setAiRecommendations(null);
    setAiRecommendationsExpanded(true); // Expand AI recommendations to show new results

    // Increment request ID to track this specific request
    aiRequestIdRef.current += 1;
    const currentRequestId = aiRequestIdRef.current;

    try {
      // Get M2M access token for external API
      const m2mToken = await getM2MAccessToken();

      // Build headers with M2M token
      const headers = {
        Accept: 'application/json',
        'Content-type': 'application/json',
        Authorization: `Bearer ${m2mToken}`,
      };
      // console.log(headers);
      // Build the request body according to the API spec
      const requestBody = {
        vendor_id: user?.id || '',
        filters: {
          available_dates: {
            start_date: startDateFilter || null,
            end_date: endDateFilter || null,
          },
          price_range: {
            min_price_per_hour: minPriceFilter ? parseFloat(minPriceFilter) : null,
            max_price_per_hour: maxPriceFilter ? parseFloat(maxPriceFilter) : null,
          },
          location: {
            state: stateFilter || null,
            city: cityFilter || null,
          },
          venue_type: indoorOutdoorFilter,
        },
      };
      // console.log(requestBody);
      // Only include non-null values in the request
      if (!requestBody.filters.available_dates.start_date) {
        delete requestBody.filters.available_dates.start_date;
      }
      if (!requestBody.filters.available_dates.end_date) {
        delete requestBody.filters.available_dates.end_date;
      }
      if (!requestBody.filters.price_range.min_price_per_hour) {
        delete requestBody.filters.price_range.min_price_per_hour;
      }
      if (!requestBody.filters.price_range.max_price_per_hour) {
        delete requestBody.filters.price_range.max_price_per_hour;
      }
      if (!requestBody.filters.location.state && !requestBody.filters.location.city) {
        delete requestBody.filters.location;
      }

      // console.log('Sending filter data to external API:', requestBody);

      const apiUrl = await getApiUrl();

      const response = await Axios.post(apiUrl, requestBody, { headers });

      // console.log('External API response:', response.data);

      // Only update recommendations if this is still the latest request
      if (currentRequestId === aiRequestIdRef.current) {
        setAiRecommendations(response.data);
      }
    } catch (error) {
      console.error('Error calling external API:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        console.error('Response data:', error.response.data);
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (currentRequestId === aiRequestIdRef.current) {
        setAiLoading(false);
      }
    }
  };

  // Clear all filters
  const clearFilters = () => {
    // Increment request ID to invalidate any pending AI requests
    aiRequestIdRef.current += 1;

    setVenueFilter('');
    setCityFilter('');
    setStateFilter('');
    setTypeFilter('both');
    setIndoorOutdoorFilter('both');
    setStartDateFilter('');
    setEndDateFilter('');
    setMinPriceFilter('');
    setMaxPriceFilter('');
    setAppliedVenueFilter('');
    setAppliedCityFilter('');
    setAppliedStateFilter('');
    setAppliedTypeFilter('both');
    setAppliedIndoorOutdoorFilter('both');
    setAppliedStartDateFilter('');
    setAppliedEndDateFilter('');
    setAppliedMinPriceFilter('');
    setAppliedMaxPriceFilter('');
    setAiRecommendations(null);
    setAiLoading(false);
    setAiRecommendationsExpanded(true);
  };

  const getPageHeaderTitle = () => {
    if (isPastEventsVisible) return 'Past Events';

    const hasEvents =
      (appliedTypeFilter === 'both' || appliedTypeFilter === 'event') &&
      filteredEventsByLocation?.length > 0;
    const hasRetail =
      (appliedTypeFilter === 'both' || appliedTypeFilter === 'retail') &&
      filteredRetailProfilesByLocation?.length > 0;

    if (hasEvents || hasRetail) {
      if (appliedTypeFilter === 'event') {
        return 'We found these events!';
      }
      if (appliedTypeFilter === 'retail') {
        return 'We found these retail spaces!';
      }
      return 'We found these opportunities!';
    }

    return '';
  };

  const getPageContent = () => {
    if (isPastEventsVisible) return <PastEvents />;

    // Don't display content until both events and retail profiles have finished loading
    if (isLoading) {
      return (
        <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    // Determine what to show based on type filter
    const showEvents = appliedTypeFilter === 'both' || appliedTypeFilter === 'event';
    const showRetail = appliedTypeFilter === 'both' || appliedTypeFilter === 'retail';

    // Calendar view - full width
    if (viewMode === 'calendar') {
      const hasEvents = showEvents && filteredEventsByLocation?.length > 0;
      const hasRetail = showRetail && filteredRetailProfilesByLocation?.length > 0;

      if (hasEvents || hasRetail) {
        let calendarTitle = 'Available Events & Retail Spaces';
        if (appliedTypeFilter === 'event') {
          calendarTitle = 'Available Events';
        } else if (appliedTypeFilter === 'retail') {
          calendarTitle = 'Retail Opportunities';
        }

        return (
          <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Grid item xs={12}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  ...(hasFiltersApplied && {
                    border: '2px solid',
                    borderColor: '#667eea',
                    boxShadow: '0 0 20px rgba(102, 126, 234, 0.3)',
                    background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.05), white)',
                  }),
                }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                    {calendarTitle}
                  </Typography>
                  {hasFiltersApplied && (
                    <Chip
                      icon={<FilterAlt />}
                      label="Filtered Results"
                      size="small"
                      sx={{
                        bgcolor: '#667eea',
                        color: 'white',
                        fontWeight: 'bold',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.8 },
                        },
                      }}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <CalendarView
                  events={showEvents ? filteredEventsByLocation : []}
                  retailProfiles={showRetail ? filteredRetailProfilesByLocation : []}
                  dateFilterStart={appliedStartDateFilter}
                  dateFilterEnd={appliedEndDateFilter}
                  priceFilterMin={appliedMinPriceFilter}
                  priceFilterMax={appliedMaxPriceFilter}
                />
              </Paper>
            </Grid>
          </Grid>
        );
      }
    }

    // Grid view - two-column layout with retail opportunities
    if (viewMode === 'grid') {
      const hasEvents = showEvents && filteredEventsByLocation?.length > 0;
      const hasRetail = showRetail && filteredRetailProfilesByLocation?.length > 0;

      if (hasEvents || hasRetail || (showEvents && showRetail)) {
        return (
          <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            {/* Events Column */}
            {showEvents && (
              <Grid item xs={12} lg={showRetail ? 8 : 12}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    ...(hasFiltersApplied && {
                      border: '2px solid',
                      borderColor: '#667eea',
                      boxShadow: '0 0 20px rgba(102, 126, 234, 0.3)',
                      background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.05), white)',
                    }),
                  }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                      Available Events
                    </Typography>
                    {hasFiltersApplied && (
                      <Chip
                        icon={<FilterAlt />}
                        label="Filtered"
                        size="small"
                        sx={{
                          bgcolor: '#667eea',
                          color: 'white',
                          fontWeight: 'bold',
                          animation: 'pulse 2s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.8 },
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {hasEvents ? (
                    <EventList events={filteredEventsByLocation} />
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No events found matching your filters.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Retail Spaces Column */}
            {showRetail && (
              <Grid item xs={12} lg={showEvents ? 4 : 12}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    ...(hasFiltersApplied && {
                      border: '2px solid',
                      borderColor: '#667eea',
                      boxShadow: '0 0 20px rgba(102, 126, 234, 0.3)',
                      background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.05), white)',
                    }),
                  }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                      Retail Opportunities
                    </Typography>
                    {hasFiltersApplied && (
                      <Chip
                        icon={<FilterAlt />}
                        label="Filtered"
                        size="small"
                        sx={{
                          bgcolor: '#667eea',
                          color: 'white',
                          fontWeight: 'bold',
                          animation: 'pulse 2s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.8 },
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {hasRetail ? (
                    <RetailSpacesList retailProfiles={filteredRetailProfilesByLocation} />
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No retail spaces found matching your filters.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        );
      }
    }

    // No results found (this only executes after both have finished loading)
    const hasEvents =
      (appliedTypeFilter === 'both' || appliedTypeFilter === 'event') &&
      filteredEventsByLocation?.length > 0;
    const hasRetail =
      (appliedTypeFilter === 'both' || appliedTypeFilter === 'retail') &&
      filteredRetailProfilesByLocation?.length > 0;

    if (!hasEvents && !hasRetail) {
      return (
        <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {appliedVenueFilter ||
                  appliedCityFilter ||
                  appliedStateFilter ||
                  appliedTypeFilter !== 'both'
                    ? 'No results found matching your filters.'
                    : 'No events or retail spaces available at this time.'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // Save to localStorage so it persists across navigation
      localStorage.setItem('vendorDashboardViewMode', newMode);
    }
  };

  // Optional: You can also save the view mode whenever it changes using useEffect
  useEffect(() => {
    localStorage.setItem('vendorDashboardViewMode', viewMode);
  }, [viewMode]);

  // Fetch retail profiles on mount so they're available in both grid and calendar views
  useEffect(() => {
    dispatch(RetailProfileActions.getRetailProfiles());
  }, [dispatch]);

  // Scroll to top when returning to the dashboard
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      {isLoading && <Loading />}
      <UserNavbar />
      <Box
        className="dashboard__banner"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundImage: `url(${fayvenDashImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}>
        <DashboardSearchBar />
        {/* <Box mt={2}>
          <CustomButton onClick={() => setIsPastEventsVisible(true)} variant="contained">
            See Past Events
          </CustomButton>
        </Box> */}
      </Box>
      <Grid container>
        <Grid item xs={12} textAlign="center">
          <Typography variant="h3" color="#EE7B57" fontWeight="bold" marginTop={4}>
            {isPastEventsVisible && (
              <IconButton onClick={() => setIsPastEventsVisible(false)}>
                <ChevronLeft sx={{ fill: '#ee7b57', width: '2em', height: '2em' }} />{' '}
              </IconButton>
            )}
            {getPageHeaderTitle()}
          </Typography>
        </Grid>

        {/* View Toggle - Only show when events or retail profiles are displayed and not in past events view */}
        {(() => {
          const hasEventsToShow =
            (appliedTypeFilter === 'both' || appliedTypeFilter === 'event') &&
            filteredEvents?.length > 0;
          const hasRetailToShow =
            (appliedTypeFilter === 'both' || appliedTypeFilter === 'retail') &&
            (!appliedVenueFilter && !appliedCityFilter && !appliedStateFilter
              ? retailProfiles?.length > 0
              : filteredRetailProfilesByLocation?.length > 0);
          return (hasEventsToShow || hasRetailToShow) && !isPastEventsVisible;
        })() && (
          <>
            {/* View Toggle - Centered */}
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop={2}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                sx={{
                  backgroundColor: 'white',
                  '& .MuiToggleButton-root': {
                    color: '#666',
                    borderColor: '#EE7B57',
                    '&.Mui-selected': {
                      backgroundColor: '#EE7B57',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#d66a47',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(238, 123, 87, 0.1)',
                    },
                  },
                }}>
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModule sx={{ marginRight: 1 }} />
                  Grid View
                </ToggleButton>
                <ToggleButton value="calendar" aria-label="calendar view">
                  <CalendarMonth sx={{ marginRight: 1 }} />
                  Calendar View
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Payment Buttons - Right Aligned */}
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
              sx={{ px: { xs: 2, md: 4 } }}>
              <LinkButton
                to={APP_ROUTES.EVENT_PAYMENTS}
                variant="contained"
                startIcon={<Payment />}
                sx={{
                  backgroundColor: '#EE7B57',
                  color: 'white',
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#d66a47',
                  },
                }}>
                Event Payments
              </LinkButton>

              <LinkButton
                to={APP_ROUTES.RETAIL_PAYMENTS}
                variant="contained"
                startIcon={<Payment />}
                sx={{
                  backgroundColor: '#3c99ba',
                  color: 'white',
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#2d7a99',
                  },
                }}>
                Retail Payments
              </LinkButton>
            </Grid>

            {/* Filters Section - Below View Toggle */}
            <Grid item xs={12} sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" marginBottom={2}>
                  <FilterList sx={{ color: '#EE7B57', marginRight: 1 }} />
                  <Typography variant="h6" color="#EE7B57" fontWeight="bold">
                    Filter Events & Retail Spaces
                  </Typography>
                </Box>

                <Grid container spacing={2} alignItems="center">
                  {/* Venue Search */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Venue Name"
                      placeholder="Venue name"
                      value={venueFilter}
                      onChange={(e) => setVenueFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* City Search */}
                  <Grid item xs={12} sm={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      placeholder="City"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* State Search */}
                  <Grid item xs={12} sm={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State"
                      placeholder="State"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* Type Filter */}
                  <Grid item xs={12} sm={3} md={1}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Type"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}>
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="event">Events</MenuItem>
                      <MenuItem value="retail">Retail Spaces</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Indoor/Outdoor Filter - Only show when retail spaces are included */}
                  {(typeFilter === 'both' || typeFilter === 'retail' || typeFilter === 'event') && (
                    <Grid item xs={12} sm={3} md={1}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Space Type"
                        value={indoorOutdoorFilter}
                        onChange={(e) => setIndoorOutdoorFilter(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#EE7B57',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EE7B57',
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#EE7B57',
                          },
                        }}>
                        <MenuItem value="both">Both</MenuItem>
                        <MenuItem value="indoor">Indoor</MenuItem>
                        <MenuItem value="outdoor">Outdoor</MenuItem>
                      </TextField>
                    </Grid>
                  )}

                  {/* Start Date Filter */}
                  <Grid item xs={12} sm={3} md={1}>
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      label="Start Date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* End Date Filter */}
                  <Grid item xs={12} sm={3} md={1}>
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      label="End Date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* Min Price Filter */}
                  <Grid item xs={12} sm={3} md={1}>
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      label="Min Price ($/hr)"
                      value={minPriceFilter}
                      onChange={(e) => setMinPriceFilter(e.target.value)}
                      inputProps={{
                        min: 0,
                        step: 1.0,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* Max Price Filter */}
                  <Grid item xs={12} sm={3} md={1}>
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      label="Max Price ($/hr)"
                      value={maxPriceFilter}
                      onChange={(e) => setMaxPriceFilter(e.target.value)}
                      inputProps={{
                        min: 0,
                        step: 1.0,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#EE7B57',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#EE7B57',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE7B57',
                        },
                      }}
                    />
                  </Grid>

                  {/* Apply Filters Button */}
                  <Grid item xs={12} sm={3} md={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="medium"
                      startIcon={<Check />}
                      onClick={applyFilters}
                      sx={{
                        backgroundColor: '#3c99ba',
                        color: 'white',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#2d7a99',
                        },
                      }}>
                      Apply
                    </Button>
                  </Grid>

                  {/* Clear Filters Button */}
                  <Grid item xs={12} sm={3} md={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="medium"
                      startIcon={<Clear />}
                      onClick={clearFilters}
                      sx={{
                        borderColor: '#EE7B57',
                        color: '#EE7B57',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#d66a47',
                          backgroundColor: 'rgba(238, 123, 87, 0.1)',
                        },
                      }}>
                      Clear
                    </Button>
                  </Grid>

                  {/* Results Count */}
                  <Grid item xs={12}>
                    <Box display="flex" gap={1} alignItems="center">
                      {(appliedTypeFilter === 'both' || appliedTypeFilter === 'event') && (
                        <Chip
                          icon={<EventIcon />}
                          label={`${filteredEventsByLocation.length} of ${
                            filteredEvents?.length || 0
                          } Events`}
                          size="small"
                          sx={{ bgcolor: '#EE7B57', color: 'white' }}
                        />
                      )}
                      {(appliedTypeFilter === 'both' || appliedTypeFilter === 'retail') && (
                        <Chip
                          icon={<Storefront />}
                          label={`${
                            filteredRetailProfilesByLocation.filter((p) => p.status === 'active')
                              .length
                          } of ${
                            (retailProfiles || []).filter((p) => p.status === 'active').length
                          } Retail Spaces`}
                          size="small"
                          sx={{ bgcolor: RETAIL_COLOR, color: 'white' }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </>
        )}

        {/* AI Recommendations Banner */}
        {(aiLoading || aiRecommendations) && (
          <Grid item xs={12} sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginBottom={2}>
                <Box display="flex" alignItems="center">
                  <AutoAwesome sx={{ marginRight: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    AI-Powered Recommendations
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setAiRecommendationsExpanded(!aiRecommendationsExpanded)}
                  sx={{ color: 'white' }}
                  size="small">
                  {aiRecommendationsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              {aiLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <Typography variant="body2">Analyzing your preferences...</Typography>
                </Box>
              )}

              {aiRecommendations && !aiLoading && aiRecommendationsExpanded && (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">
                      Total Recommendations:{' '}
                      {Math.min(
                        aiRecommendations.recommendations?.events?.recommendations?.length || 0,
                        3,
                      ) +
                        Math.min(
                          aiRecommendations.recommendations?.retail_spaces?.recommendations
                            ?.length || 0,
                          3,
                        )}
                    </Typography>
                    <Tabs
                      value={aiRecommendationsTab}
                      onChange={(e, newValue) => setAiRecommendationsTab(newValue)}
                      sx={{
                        minHeight: 'auto',
                        '& .MuiTabs-indicator': {
                          backgroundColor: 'white',
                        },
                        '& .MuiTab-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          minHeight: 'auto',
                          py: 0.5,
                          textTransform: 'none',
                          '&.Mui-selected': {
                            color: 'white',
                          },
                        },
                      }}>
                      <Tab
                        label={`Events (${Math.min(
                          aiRecommendations.recommendations?.events?.recommendations?.length || 0,
                          3,
                        )})`}
                        value="events"
                      />
                      <Tab
                        label={`Retail (${Math.min(
                          aiRecommendations.recommendations?.retail_spaces?.recommendations
                            ?.length || 0,
                          3,
                        )})`}
                        value="retail"
                      />
                    </Tabs>
                  </Box>

                  <Grid container spacing={2}>
                    {/* Event Recommendations */}
                    {aiRecommendationsTab === 'events' &&
                      aiRecommendations.recommendations?.events?.recommendations?.length > 0 && (
                        <>
                          {aiRecommendations.recommendations.events.recommendations
                            .slice(0, 3)
                            .map((recommendation) => {
                              const isTopPick =
                                aiRecommendations.recommendations?.top_recommendation?.event_id ===
                                recommendation.event_id;
                              return (
                                <Grid
                                  item
                                  xs={12}
                                  md={4}
                                  key={`event-${recommendation.event_id}-${recommendation.data?.event_name}`}>
                                  <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                                    <CardContent>
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        marginBottom={1}
                                        flexWrap="wrap"
                                        gap={1}>
                                        <Box display="flex" gap={1}>
                                          <Chip
                                            icon={<EventIcon />}
                                            label="Event"
                                            size="small"
                                            sx={{ bgcolor: '#EE7B57', color: 'white' }}
                                          />
                                          {isTopPick && (
                                            <Chip
                                              icon={<AutoAwesome />}
                                              label="Top Pick"
                                              size="small"
                                              sx={{
                                                bgcolor: '#FFD700',
                                                color: '#000',
                                                fontWeight: 'bold',
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Chip
                                          label={`Score: ${recommendation.score}`}
                                          size="small"
                                          sx={{
                                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'white',
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        marginBottom={1}
                                        sx={{ color: 'white' }}>
                                        {recommendation.data?.event_name}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        marginBottom={1}
                                        sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                        {recommendation.recommendations}
                                      </Typography>
                                      <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {recommendation.data?.venue_city && (
                                          <Chip
                                            label={recommendation.data.venue_city}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                        {recommendation.data?.event_date && (
                                          <Chip
                                            label={moment(recommendation.data.event_date).format(
                                              'MMM D',
                                            )}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box display="flex" gap={1} mt={2}>
                                        <LinkButton
                                          to={APP_ROUTES.EVENT_DETAILS.replace(
                                            ':eventId',
                                            recommendation.event_id,
                                          )}
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            color: 'white',
                                            textTransform: 'none',
                                            '&:hover': {
                                              borderColor: 'white',
                                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                          }}>
                                          View Details
                                        </LinkButton>
                                        <LinkButton
                                          to={APP_ROUTES.VENDOR_EVENT_REGISTRATION.replace(
                                            ':id',
                                            recommendation.event_id,
                                          )}
                                          variant="contained"
                                          size="small"
                                          sx={{
                                            bgcolor: '#EE7B57',
                                            color: 'white',
                                            textTransform: 'none',
                                            '&:hover': {
                                              bgcolor: '#d66a47',
                                            },
                                          }}>
                                          Apply
                                          <ChevronRight />
                                        </LinkButton>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </>
                      )}

                    {/* Retail Space Recommendations */}
                    {aiRecommendationsTab === 'retail' &&
                      aiRecommendations.recommendations?.retail_spaces?.recommendations?.length >
                        0 && (
                        <>
                          {aiRecommendations.recommendations.retail_spaces.recommendations
                            .slice(0, 3)
                            .map((recommendation) => {
                              const isTopPick =
                                aiRecommendations.recommendations?.top_recommendation
                                  ?.retail_schedule_id === recommendation.retail_schedule_id;

                              // Format the title with venue name and day
                              const venueName = recommendation.data?.venue_name || 'Retail Space';
                              const dayOfWeek = recommendation.data?.day_of_week
                                ? recommendation.data.day_of_week.charAt(0).toUpperCase() +
                                  recommendation.data.day_of_week.slice(1)
                                : '';
                              const spaceType = recommendation.data?.space_type
                                ? `${recommendation.data.space_type
                                    .charAt(0)
                                    .toUpperCase()}${recommendation.data.space_type.slice(1)}`
                                : '';

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  md={4}
                                  key={`retail-${
                                    recommendation.retail_schedule_id || ''
                                  }-${dayOfWeek}`}>
                                  <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                                    <CardContent>
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        marginBottom={1}
                                        flexWrap="wrap"
                                        gap={1}>
                                        <Box display="flex" gap={1}>
                                          <Chip
                                            icon={<Storefront />}
                                            label="Retail"
                                            size="small"
                                            sx={{ bgcolor: '#3c99ba', color: 'white' }}
                                          />
                                          {isTopPick && (
                                            <Chip
                                              icon={<AutoAwesome />}
                                              label="Top Pick"
                                              size="small"
                                              sx={{
                                                bgcolor: '#FFD700',
                                                color: '#000',
                                                fontWeight: 'bold',
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Chip
                                          label={`Score: ${recommendation.score}`}
                                          size="small"
                                          sx={{
                                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'white',
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        marginBottom={1}
                                        sx={{ color: 'white' }}>
                                        {venueName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        marginBottom={1}
                                        sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                        {recommendation.match_reasons?.join(', ') ||
                                          recommendation.recommendations}
                                      </Typography>
                                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                                        {dayOfWeek && (
                                          <Chip
                                            label={dayOfWeek}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                        {spaceType && (
                                          <Chip
                                            label={spaceType}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                        {recommendation.data?.price_per_hour && (
                                          <Chip
                                            label={`$${recommendation.data.price_per_hour}/hr`}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {recommendation.data?.venue_city && (
                                          <Chip
                                            icon={<LocationOn sx={{ fontSize: 16 }} />}
                                            label={recommendation.data.venue_city}
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box display="flex" justifyContent="flex-end" mt={2}>
                                        <LinkButton
                                          to={APP_ROUTES.RETAIL_OPPORTUNITY_DETAILS.replace(
                                            ':profileId',
                                            recommendation.retail_schedule_id,
                                          )}
                                          variant="contained"
                                          size="small"
                                          sx={{
                                            bgcolor: '#3c99ba',
                                            color: 'white',
                                            textTransform: 'none',
                                            '&:hover': {
                                              bgcolor: '#2d7a99',
                                            },
                                          }}>
                                          View Details & Apply
                                          <ChevronRight />
                                        </LinkButton>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </>
                      )}
                  </Grid>
                </>
              )}
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          {getPageContent()}
        </Grid>
      </Grid>
    </Layout>
  );
}

export default VendorDashboard;
